<template>
    <div class="nav-profil-menu__user-container">
        <CompassMenu placement="bottom-end" customClass="nav-profil-menu__menu">
            <CompassTooltip :content="$t.__('header.Nav.menu')" :arrow="false">
                <div class="nav-profil-menu__user--container">
                    <div class="nav-profil-menu__user">
                        <CompassAvatar
                            :initials="getInitialsAvatar"
                            :photo="getSourceAvatar"
                            size="small"
                            class="nav-profil-menu__user--profil-avatar"
                        />
                        <div class="nav-profil-menu__user--profil">
                            <div
                                class="typographies-paragraph-medium-semibold nav-profil-menu__user--profil-name"
                            >
                                {{ fullName }}
                            </div>
                            <div
                                v-if="isStaff"
                                class="nav-profil-menu__user--profil-corp typographies-paragraph-small-semibold nav-profil-menu__user--profil-name"
                            >
                                {{ corpName }}
                            </div>
                        </div>
                    </div>
                    <CompassIcon icon="caret-down" size="small" />
                </div>
            </CompassTooltip>
            <template #dropdown>
                <CompassMenuItem
                    v-if="isStaff"
                    :label="translations.setups"
                    prefixIcon="settings"
                    @click="handleMenu('setups', $event)"
                />
                <CompassMenuItem
                    :label="translations.profile"
                    prefixIcon="profile"
                    @click="handleMenu('profile', $event)"
                />
                <CompassMenuItem
                    v-if="isStaff && isAdmin"
                    :label="translations.subscription"
                    prefixIcon="subscription"
                    @click="handleMenu('subscription', $event)"
                />
                <CompassMenuItem
                    v-if="isStaff && userData.canAccessActionHistory"
                    :label="translations.history"
                    prefixIcon="history"
                    @click="handleMenu('history', $event)"
                />
                <hr v-if="isStaff" class="nav-profil-menu__divider" />
                <CompassMenuItem
                    v-if="automationsAllowed"
                    :label="translations.automation"
                    prefixIcon="automation"
                    :flagProps="{ label: $t.__('common.labels.tag.new') }"
                    dataBot="menu-burger__automation"
                    @click="handleMenu('automation', $event)"
                />
                <CompassMenuItem
                    v-if="isStaff && isAdmin"
                    :label="translations.modules"
                    prefixIcon="modules"
                    @click="handleMenu('modules', $event)"
                />
                <CompassMenuItem
                    v-if="isStaff"
                    :label="translations.marketplace"
                    prefixIcon="marketplace"
                    @click="handleMenu('marketplace', $event)"
                >
                    <span class="typographies-paragraph-medium-regular">
                        {{ translations.marketplace }}
                    </span>
                    <CompassIcon
                        icon="external-link"
                        size="small"
                        class="nav-profil-menu__external-icon"
                    />
                </CompassMenuItem>
                <hr v-if="isStaff" class="nav-profil-menu__divider" />
                <CompassMenuItem
                    v-if="isStaff"
                    :label="translations.help"
                    prefixIcon="help-circle"
                    @click="handleMenu('help', $event)"
                >
                    <span class="typographies-paragraph-medium-regular">
                        {{ translations.help }}
                    </span>
                    <CompassIcon
                        icon="external-link"
                        size="small"
                        class="nav-profil-menu__external-icon"
                    />
                </CompassMenuItem>
                <CompassMenuItem
                    v-if="meltingspotAllowed"
                    :label="translations.academy"
                    @click="handleMenu('academy', $event)"
                >
                    <template #prefixIcon>
                        <MeltingSpotWidget decorative />
                    </template>
                </CompassMenuItem>
                <template v-if="hasMultipleAccounts">
                    <hr class="nav-profil-menu__divider" />
                    <template v-if="availableAccounts.length <= 3">
                        <CompassMenuSubtitle :label="translations.switchAccount" />
                        <CompassMenuItem
                            v-for="account in availableAccounts"
                            :key="account.uuid"
                            :label="getAccountDisplayName(account)"
                            class="nav-profil-menu__account-item"
                            :tooltipProps="{
                                placement: 'bottom',
                                content: getAccountDisplayName(account),
                                delay: { show: 1000, hide: 0 },
                                arrow: false,
                                contentClass: 'nav-profil-menu__tooltip-content',
                            }"
                            @click="switchToAccount(account)"
                        />
                    </template>
                    <CompassMenuGroup
                        v-else
                        :label="translations.switchAccount"
                        icon="sync"
                        placement="left-start"
                        trigger="click"
                        customClass="nav-profil-menu__menu-group"
                    >
                        <template #dropdown>
                            <CompassMenuItem
                                v-for="account in availableAccounts"
                                :key="account.uuid"
                                :label="getAccountDisplayName(account)"
                                @click="switchToAccount(account)"
                            />
                        </template>
                    </CompassMenuGroup>
                </template>
                <hr class="nav-profil-menu__divider" />
                <CompassMenuItem
                    :label="translations.logout"
                    prefixIcon="logout"
                    class="nav-profil-menu__item--logout"
                    @click="handleMenu('logout', $event)"
                />
            </template>
        </CompassMenu>
    </div>
</template>

<script>
import { goToUrl } from 'vueDir/helpers.js';
import { createNamespacedHelpers } from 'vuex';
import Feature from 'Services/Feature';
import MeltingSpotWidget from 'vcomponents/academy/MeltingSpotWidget.vue';

const corpMapState = createNamespacedHelpers('corp').mapState;
const userMapState = createNamespacedHelpers('user').mapState;
const userMapGetters = createNamespacedHelpers('user').mapGetters;

export default {
    name: 'NavProfilMenu',
    components: {
        MeltingSpotWidget,
    },
    props: {
        userData: {
            type: Object,
            required: true,
        },
        urlsData: {
            type: Object,
            required: true,
        },
        lang: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            ctrlKeyPressed: false,
            translations: {
                setups: this.$t.__('common.labels.setups'),
                profile: this.$t.__('common.labels.profile'),
                subscription: this.$t.__('common.labels.manage_subscription'),
                history: this.$t.__('common.labels.history'),
                automation: this.$t.__('common.labels.automation'),
                modules: this.$t.__('common.labels.modules'),
                marketplace: this.$t.__('common.labels.marketplace'),
                help: this.$t.__('common.labels.help'),
                academy: this.$t.__('common.labels.sellsy_academy'),
                switchAccount: this.$t.__('common.labels.switch_account'),
                logout: this.$t.__('common.labels.logout'),
                clientSpace: this.$t.__('common.labels.client_space'),
            },
        };
    },
    computed: {
        ...corpMapState({
            corpName: 'name',
        }),
        ...userMapState(['forename', 'name', 'type']),
        ...userMapGetters(['getUser']),
        fullName() {
            return `${this.forename} ${this.name}`;
        },
        isStaff() {
            return this.type === 'staff';
        },
        isAdmin() {
            return this.userData.isAdmin;
        },
        automationsAllowed() {
            return Feature.isEnabled('automations');
        },
        hasFullAutomationAccess() {
            return this.isAdmin || this.getUser.permissions?.acl?.manage_automations === true;
        },
        meltingspotAllowed() {
            return this.userData.meltingspotAllowed;
        },
        getInitialsAvatar() {
            return this.userData.avatar.type === 'initials'
                ? this.userData.avatar.value.charAt(0)
                : '';
        },
        getSourceAvatar() {
            return this.userData.avatar.type === 'picture' ? this.userData.avatar.value : '';
        },
        availableAccounts() {
            return this.userData.accounts
                .filter((account) => account.externalUserId !== Number(this.userData.currentUserId))
                .sort((a, b) => {
                    const nameA = this.getAccountDisplayName(a).toLowerCase();
                    const nameB = this.getAccountDisplayName(b).toLowerCase();
                    return nameA.localeCompare(nameB);
                });
        },
        hasMultipleAccounts() {
            return this.availableAccounts.length > 0;
        },
    },
    mounted() {
        window.addEventListener('keydown', this.handleKeyDown);
        window.addEventListener('keyup', this.handleKeyUp);
    },
    beforeUnmount() {
        window.removeEventListener('keydown', this.handleKeyDown);
        window.removeEventListener('keyup', this.handleKeyUp);
    },
    methods: {
        logout() {
            try {
                window.Intercom('shutdown');
            } catch (e) {
                // Intercom not loaded
            }
            return goToUrl('/logout');
        },
        switchToAccount(account) {
            goToUrl(account.switchUrl);
        },
        getAccountDisplayName(account) {
            const isThird = account.externalUserType === 'third';
            const thirdName =
                isThird && account.externalAttributes?.third_name
                    ? ` - ${account.externalAttributes.third_name}`
                    : '';
            const customerName = account.customer?.name || '';
            const thirdSuffix = isThird ? ` ( ${this.translations.clientSpace} )` : '';

            return `${customerName}${thirdName}${thirdSuffix}`;
        },
        handleKeyDown(event) {
            if (this.isModifierKey(event)) {
                this.ctrlKeyPressed = true;
            }
        },
        handleKeyUp(event) {
            if (!this.isModifierKey(event)) {
                this.ctrlKeyPressed = false;
            }
        },
        isModifierKey(event) {
            return event.ctrlKey || event.metaKey;
        },
        handleMenu(type, event) {
            const openInNewTab = this.isCtrlOrCmdPressed(event);
            if (openInNewTab) {
                this.openInNewTab(type);
                return;
            }
            this.executeMenuAction(type);
        },
        isCtrlOrCmdPressed(event) {
            const originalEvent = event?.originalEvent || event;
            return (
                this.ctrlKeyPressed ||
                (originalEvent && (originalEvent.ctrlKey || originalEvent.metaKey)) ||
                (event && (event.ctrlKey || event.metaKey))
            );
        },
        openInNewTab(type) {
            const url = this.getMenuUrl(type);
            if (url) {
                window.open(url, '_blank');
            }
        },
        getMenuUrl(type) {
            const urls = {
                setups: this.$router.resolve({ name: 'settings' }).href,
                profile: this.isStaff ? this.urlsData.staff : this.urlsData.extProfile,
                subscription: '/?_f=accountPreferences&action=subscription',
                automation: this.hasFullAutomationAccess
                    ? '/automations/listing'
                    : '/automations/templates',
                modules: this.urlsData.legacy,
                marketplace: 'https://marketplace.sellsy.com/',
                help: `https://help.sellsy.com/hc/?lang=${this.lang}`,
                academy: '/sellsy-academy',
            };
            return urls[type];
        },
        executeMenuAction(type) {
            switch (type) {
                case 'setups':
                    this.$router.push({ name: 'settings' });
                    break;
                case 'profile':
                    goToUrl(this.isStaff ? this.urlsData.staff : this.urlsData.extProfile);
                    break;
                case 'subscription':
                    goToUrl('/?_f=accountPreferences&action=subscription');
                    break;
                case 'history':
                    window.LogEngine.loadPopup(
                        'dashboard',
                        '0',
                        this.$l10n.__('Historique des actions du compte'),
                    );
                    break;
                case 'automation':
                    this.hasFullAutomationAccess
                        ? goToUrl('/automations/listing')
                        : goToUrl('/automations/templates');
                    break;
                case 'modules':
                    goToUrl(this.urlsData.legacy);
                    break;
                case 'marketplace':
                    window.open('https://marketplace.sellsy.com/', '_blank');
                    break;
                case 'help':
                    window.open(`https://help.sellsy.com/hc/?lang=${this.lang}`, '_blank');
                    break;
                case 'academy':
                    goToUrl('/sellsy-academy');
                    break;
                case 'logout':
                    this.logout();
                    break;
                default:
                    break;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.nav-profil-menu {
    &__user {
        max-width: 172px;
        display: flex;
        gap: var(--spacing-1);
        align-items: center;

        &--container {
            display: flex;
            align-items: center;
            padding: var(--spacing-0);
            gap: var(--spacing-2);
            border-radius: var(--spacing-1);
            color: var(--colors-common-text-standard-default);
            cursor: pointer;

            &:hover {
                background-color: var(--colors-common-background-hover);
            }

            @media screen and (width < 1140px) {
                gap: 0;
            }
        }

        &-container {
            flex-shrink: 0;
        }

        &--profil {
            color: var(--colors-common-text-standard-default);
            &-corp {
                color: var(--colors-common-text-standard-caption);
            }
            &-name {
                @media screen and (width < 1140px) {
                    display: none;
                }
            }
            & > div {
                text-overflow: ellipsis;
                max-width: 125px;
                overflow: hidden;
                white-space: nowrap;
            }
        }

        @media screen and (width <= 1439px) {
            gap: var(--spacing-1);
            padding: var(--spacing-0);
        }
    }
    &__divider {
        height: 1px;
        margin: 7.5px var(--spacing-1);
        border: 0;
        border-top: 1px solid var(--colors-common-border-lighter);
    }
    &__tooltip-content {
        z-index: 1003 !important;
    }
    &__external-icon {
        opacity: 0;
        transition: opacity 0.2s ease-in-out;
    }
    :deep(.compass-tooltip__element) {
        display: block;
    }
}
</style>

<style lang="scss">
.compass-menu__dropdown__container.nav-profil-menu__menu {
    z-index: 1001;

    .compass-menu__dropdown__content {
        max-height: none;
        &__options-container {
            gap: 0;
        }
    }
}
.compass-menu-group__dropdown__container.nav-profil-menu__menu-group {
    z-index: 1002;

    .compass-menu-group__dropdown__content {
        max-width: 230px;
        max-height: 540px;
        overflow-y: auto;
    }
}
.compass-menu-item.nav-profil-menu__item--logout {
    color: var(--colors-sellsy-red-600);
    &:hover {
        background-color: var(--colors-common-background-alert-transparent-hover) !important;
    }
}

.compass-menu-item:hover .nav-profil-menu__external-icon {
    opacity: 1;
}

.compass-menu-item.nav-profil-menu__account-item {
    .compass-menu-item__text {
        max-width: 200px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}

.nav-profil-menu__tooltip-content {
    z-index: 1003 !important;
}

.compass-tooltip:has(.nav-profil-menu__account-item) .compass-tooltip__element,
.compass-tooltip .compass-tooltip__element:has(.nav-profil-menu__account-item),
.compass-tooltip__element:has(.nav-profil-menu__account-item) {
    display: block;
}

.nav-profil-menu__user--profil-avatar .compass-avatar__container {
    background-color: var(--colors-sellsy-brand-primary-100);
}
</style>
